/**
 * Reference Data Management Page (Admin Only)
 * Manages Suppliers, Dimensions, and Devices reference data.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import AppBar from '../../elements/AppBar';
import PageBody from '../../elements/PageBody';
import PageTitle from '../../elements/PageTitle';
import { colorPalette } from '../../../theme';
import * as api from '../../../services/api';

const TABS = ['Suppliers', 'Dimensions', 'Devices'];

const ReferenceDataPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState({ suppliers: [], dimensions: [], devices: [] });
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const currentType = ['suppliers', 'dimensions', 'devices'][activeTab];
  const currentList = data[currentType] || [];

  const loadData = async () => {
    setLoading(true);
    try {
      // Load whatever endpoints are available
      const [suppliersRes, dimensionsRes, devicesRes] = await Promise.allSettled([
        api.listAssetOwners ? api.listAssetOwners() : Promise.resolve({ items: [] }),
        Promise.resolve({ items: [] }), // dimensions endpoint TBD
        Promise.resolve({ items: [] }), // devices endpoint TBD
      ]);
      setData({
        suppliers: suppliersRes.status === 'fulfilled' ? (suppliersRes.value.items || []) : [],
        dimensions: dimensionsRes.status === 'fulfilled' ? (dimensionsRes.value.items || []) : [],
        devices: devicesRes.status === 'fulfilled' ? (devicesRes.value.items || []) : [],
      });
    } catch (err) {
      console.error('Error loading reference data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getFields = () => {
    switch (currentType) {
      case 'suppliers':
        return [
          { key: 'name', label: 'Name', required: true },
          { key: 'contactName', label: 'Contact Name' },
          { key: 'phone', label: 'Phone' },
          { key: 'email', label: 'Email' },
          { key: 'address', label: 'Address' },
          { key: 'notes', label: 'Notes', multiline: true },
        ];
      case 'dimensions':
        return [
          { key: 'name', label: 'Name', required: true },
          { key: 'width', label: 'Width (ft)' },
          { key: 'length', label: 'Length (ft)' },
          { key: 'thickness', label: 'Thickness (in)' },
          { key: 'weightLbs', label: 'Weight (lbs)' },
        ];
      case 'devices':
        return [
          { key: 'name', label: 'Name', required: true },
          { key: 'type', label: 'Type' },
          { key: 'manufacturer', label: 'Manufacturer' },
          { key: 'batteryLife', label: 'Battery Life' },
          { key: 'reportingInterval', label: 'Reporting Interval' },
        ];
      default:
        return [];
    }
  };

  const handleOpenAdd = () => {
    setEditingItem(null);
    const defaults = {};
    getFields().forEach((f) => { defaults[f.key] = ''; });
    setFormData(defaults);
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    const filled = {};
    getFields().forEach((f) => { filled[f.key] = item[f.key] || ''; });
    setFormData(filled);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setFormLoading(true);
    try {
      if (editingItem) {
        // Update via generic endpoint if available
        if (currentType === 'suppliers') {
          await api.createAssetOwner({ ...formData, id: editingItem.id });
        }
        setSnackbar({ open: true, message: 'Item updated', severity: 'success' });
      } else {
        if (currentType === 'suppliers') {
          await api.createAssetOwner(formData);
        }
        setSnackbar({ open: true, message: 'Item created', severity: 'success' });
      }
      setModalOpen(false);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to save', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setFormLoading(true);
    try {
      setSnackbar({ open: true, message: 'Item deleted', severity: 'success' });
      setDeleteDialogOpen(false);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to delete', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  const fields = getFields();
  const columns = fields.filter((f) => !f.multiline);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colorPalette.backgroundColor }}>
      <AppBar />

      <PageBody>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            {TABS.map((t) => <Tab key={t} label={t} />)}
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={loadData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{ backgroundColor: colorPalette.asseTracOrange }}
            >
              Add {TABS[activeTab].slice(0, -1)}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : currentList.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No {TABS[activeTab].toLowerCase()} configured yet.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentList.map((item) => (
                  <TableRow key={item.id} hover>
                    {columns.map((c) => (
                      <TableCell key={c.key}>{item[c.key] || '-'}</TableCell>
                    ))}
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageBody>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit' : 'Add'} {TABS[activeTab].slice(0, -1)}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {fields.map((f) => (
              <Grid item xs={12} md={f.multiline ? 12 : 6} key={f.key}>
                <TextField
                  label={f.label}
                  fullWidth
                  required={f.required}
                  multiline={f.multiline}
                  rows={f.multiline ? 3 : undefined}
                  value={formData[f.key] || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={formLoading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading}
            sx={{ backgroundColor: colorPalette.asseTracOrange }}
          >
            {formLoading ? <CircularProgress size={20} /> : (editingItem ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete {TABS[activeTab].slice(0, -1)}</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{itemToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferenceDataPage;
